// Product tour (Type B, account-native). Hub sections + spotlight copy.
// Voice: no em dashes. Land Health is a 1-9 level + band, never a %.

export const PRODUCT_TOUR_STATUS = Object.freeze({
  PENDING: 'pending',
  SKIPPED: 'skipped',
  COMPLETED: 'completed',
});

/** True until the user takes the tour or dismisses the welcome offer. */
export function isTourOfferOpen(status) {
  return status !== PRODUCT_TOUR_STATUS.SKIPPED
    && status !== PRODUCT_TOUR_STATUS.COMPLETED;
}

/** Auto-offer on normal app pages; skip checkout, intake, and admin consoles. */
export function shouldAutoOfferTour(pathname) {
  const path = pathname || '';
  if (path === '/checkout' || path.startsWith('/checkout')) return false;
  if (path === '/success' || path === '/cancel') return false;
  if (path.startsWith('/properties/new')) return false;
  if (path.startsWith('/admin')) return false;
  if (path.startsWith('/superuser')) return false;
  return true;
}

/** MapViewer listens and opens collapsed / unmounted panels so every map step can run. */
export const PRODUCT_TOUR_PREPARE_MAP = 'ei-product-tour-prepare-map';

/** Fired when a tour section's spotlight closes, so the map can resume auto-collapsing the layer nav. */
export const PRODUCT_TOUR_ENDED = 'ei-product-tour-ended';

/** Concertina pages expand minimised cards so feature steps can highlight real content. */
export const PRODUCT_TOUR_PREPARE_CONCERTINA = 'ei-product-tour-prepare-concertina';

export function farmIdFromPath(pathname) {
  const m = /^\/(?:overview|assessments|diagnostics|actions|map|analytics|reports|disclosure|setup|credentials)\/([^/]+)/.exec(pathname || '');
  return m ? decodeURIComponent(m[1]) : null;
}

export const TOUR_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    blurb: 'Your properties and the way into each view.',
    needsProperty: false,
    path: () => '/dashboard',
    readySelector: '[data-tour="dash-welcome"]',
  },
  {
    id: 'overview',
    label: 'Overview',
    blurb: 'Land Health verdict, processes, and the layers behind them.',
    needsProperty: true,
    path: (farmId) => `/overview/${farmId}`,
    readySelector: '[data-tour="card-cover"]',
    prepareEvent: PRODUCT_TOUR_PREPARE_CONCERTINA,
  },
  {
    id: 'map',
    label: 'Map',
    blurb: 'Interactive layers, parcels, and the Land Health legend.',
    needsProperty: true,
    path: (farmId) => `/map/${farmId}`,
    readySelector: '[data-tour="map-layers"]',
    prepareEvent: PRODUCT_TOUR_PREPARE_MAP,
    prepareSelectors: ['[data-tour="map-active-layers"]', '[data-tour="map-inspector"]'],
  },
  {
    id: 'disclosure',
    label: 'Disclosure',
    blurb: 'Frameworks, readiness, and evidence you can lift.',
    needsProperty: true,
    path: (farmId) => `/disclosure/${farmId}`,
    readySelector: '[data-tour="card-disc"]',
    prepareEvent: PRODUCT_TOUR_PREPARE_CONCERTINA,
  },
  {
    id: 'actions',
    label: 'Actions',
    blurb: 'The Field Forward Plan action plan and supporting site-wide moves.',
    needsProperty: true,
    path: (farmId) => `/actions/${farmId}`,
    readySelector: '[data-tour="action-plan"]',
    prepareEvent: PRODUCT_TOUR_PREPARE_CONCERTINA,
  },
  {
    id: 'reports',
    label: 'Reports',
    blurb: 'Published report packages and the status badge.',
    needsProperty: true,
    path: (farmId) => `/reports/${farmId}`,
    readySelector: '[data-tour="reports-hub"]',
  },
  {
    id: 'credentials',
    label: 'Credentials',
    blurb: 'Land Health badge QR, the public register, and the credential page.',
    needsProperty: true,
    path: (farmId) => `/credentials/${farmId}`,
    readySelector: '[data-tour="credentials-hub"]',
  },
  {
    id: 'setup',
    label: 'Setup',
    blurb: 'Property details, data exchange, and connected services.',
    needsProperty: true,
    path: (farmId) => `/setup/${farmId}`,
    readySelector: '[data-tour="setup-details"]',
  },
  {
    id: 'glossary',
    label: 'Help and glossary',
    blurb: 'Terms, scores, and the search that sits on every page.',
    needsProperty: false,
    path: null,
    readySelector: '[data-tour="nav-help"]',
  },
];

function step(element, title, description, extra = {}) {
  return { element, title, description, ...extra };
}

const SECTION_STEPS = {
  dashboard: [
    step('[data-tour="dash-welcome"]', 'Your dashboard',
      'This is home after you sign in. Pick a property here to open Overview, Map, Reports and the rest.'),
    step('[data-tour="dash-properties"]', 'Your properties',
      'Each card is a property. Demo properties are shared examples you can explore before your own land is ready.',
      { optional: true }),
    step('[data-tour="dash-get-started"]', 'Add your own property',
      'When you are ready, start from this card (or Add a property) to submit the details we need to build your site.',
      { optional: true }),
    step('[data-tour="dash-add-property"]', 'Add a property',
      'Use this when you already have at least one property and want to add another.',
      { optional: true }),
    step('[data-tour="nav-help"]', 'Help is always here',
      'The question mark opens Help and glossary on every page. You can replay this tour from there at any time.'),
    step('[data-tour="dash-properties"]', 'Open a property to go further',
      'Click a property card (or its Overview link) to unlock Overview, Map, Disclosure, Actions, Reports and Setup in the top tabs.',
      { optional: true }),
  ],
  overview: [
    step('[data-tour="nav-overview"]', 'Overview tab',
      'Overview is the property landing page. The other tabs sit beside it once a property is open.'),
    step('[data-tour="property-header"]', 'Property header',
      'This strip stays with you on every property page: name, Land Health as a 1 to 9 level and band, and trajectory.'),
    step('[data-tour="card-cover"]', 'Property overview',
      'The cover card is the status snapshot for the property: the published badge and the headline verdict.'),
    step('[data-tour="card-proc"]', 'Four ecosystem processes',
      'Water, Energy, Mineral and Community. Each is a 1 to 9 level. The lowest is the limiting process.'),
    step('[data-tour="card-summary"]', 'Summary',
      'A short readout of what the published run found, with a path into Reports when you want the full document.'),
    step('[data-tour="card-edx"]', 'Land health classification',
      'Each parcel coloured by its Land Health band. Greener is healthier. The site level is cited as a 1 to 9 band, never a percentage.'),
    step('[data-tour="card-carbon"]', 'Carbon balance',
      'Indicative photosynthesis versus respiration. This supports a conversation; it is not a verified carbon credit.'),
    step('[data-tour="card-map"]', 'Site parcel map',
      'Field boundaries on imagery. Open the interactive Map from here (or the Map tab) to turn layers on and off.'),
  ],
  map: [
    step('[data-tour="nav-map"]', 'Map tab',
      'The interactive map is where you explore layers on the land, parcel by parcel.'),
    step('[data-tour="map-layers"]', 'Map layers',
      'Quick views and the explorer live here. Turn on terrain, water, vegetation and more. Drag panels if they overlap.'),
    step('[data-tour="map-active-layers"]', 'Active layers',
      'Once a raster is on, this stack lets you reorder, set opacity and remove layers. The tour turns Land Health on so you can see it.',
      { optional: true }),
    step('[data-tour="map-inspector"]', 'Parcel inspector',
      'A field is opened here so you can see scores and details. Click any parcel yourself later. You can rename parcels when you have edit access.',
      { optional: true }),
    step('[data-tour="map-edx-legend"]', 'Land Health legend',
      'The 1 to 9 Land Health bands. Match the colours on the map to the level and band name, not a percentage.'),
  ],
  disclosure: [
    step('[data-tour="nav-disclosure"]', 'Disclosure tab',
      'Disclosure is informed by the published run. It helps you see what you can lift into a framework; it is not an assurance statement.'),
    step('[data-tour="disclosure-docs"]', 'Documentation',
      'Open this for the claim language: informed by and supports, never compliant or certified.'),
    step('[data-tour="disclosure-intro"]', 'What this page is for',
      'One satellite assessment, mapped across the frameworks your buyers or ESG reporting use. Dismiss the banner when you have read it.',
      { optional: true }),
    step('[data-tour="card-disc-choose"]', 'Frameworks you work with',
      'Switch on the frameworks you care about and save. The rest of the page follows that choice.',
      { optional: true }),
    step('[data-tour="card-disc"]', 'Disclosure readiness',
      'How far the published evidence goes. Status language is honest: evidenced, documented, positioned, baseline.'),
    step('[data-tour="card-disc-honesty"]', 'How it works',
      'What you can and cannot say. Use "informed by" and "supports", never "compliant" or "certified".'),
    step('[data-tour="card-disc-actions"]', 'Disclosure actions',
      'Outstanding moves on the frameworks that are switched on: what still needs a lift or operator evidence.'),
    step('[data-tour="card-disc-fw"]', 'Frameworks and evidence',
      'Each framework row shows status and the evidence behind it. Lift a baseline row to Documented when you have a management representation.',
      { optional: true }),
    step('[data-tour="card-tnfd-metrics"]', 'TNFD metrics',
      'Nature-related metrics drawn from the published payload, when TNFD is switched on.',
      { optional: true }),
    step('[data-tour="card-disc-route"]', 'Report once, map to many',
      'How one assessment maps across the frameworks you switched on, including what still needs operator evidence.',
      { optional: true }),
  ],
  actions: [
    step('[data-tour="nav-actions"]', 'Actions tab',
      'Actions prints the Field Forward Plan action plan as published, not a to-do list invented in the app.'),
    step('[data-tour="action-plan"]', 'Action plan',
      'Fields in the published priority order, with a scenario switch, outcome filters, and the move for each field.'),
    step('[data-tour="card-ac-site"]', 'Site-wide actions',
      'Whole-property moves: the first things to consider across the land, written from the published Actions surface.',
      { optional: true }),
  ],
  reports: [
    step('[data-tour="nav-reports"]', 'Reports tab',
      'Published report packages live here. The app displays what ReportBuilder pushed; it does not rebuild the PDF.'),
    step('[data-tour="reports-share"]', 'Share your status',
      'When a status badge has been published, you can open a shareable snapshot of Land Health for this property.',
      { optional: true }),
    step('[data-tour="reports-note"]', 'On-demand reports',
      'Full reports are run on request against a dedicated reporting framework. This page is the library of what has already been published.'),
    step('[data-tour="reports-packages"]', 'Report packages',
      'Each group is a suite (Land Health, sustainability and others). Open a tile to read the HTML or download the PDF.'),
    step('[data-tour="reports-editions"]', 'Year editions',
      'Each tile lists year editions. Locked years follow your subscription; demo properties stay readable so you can see the suite.'),
  ],
  credentials: [
    step('[data-tour="nav-credentials"]', 'Credentials tab',
      'This is where you choose whether the Land Health badge carries a QR code, and what appears on the public credential page.'),
    step('[data-tour="credentials-status"]', 'Issued status',
      'The code, dates and register status come from ReportBuilder. The app never invents a code.'),
    step('[data-tour="credentials-hub"]', 'Publication and flavour',
      'Switch on the QR to join the public register. Farmer-authored photo and copy are reviewed by EcoIntel before they go live.'),
  ],
  setup: [
    step('[data-tour="nav-setup"]', 'Setup tab',
      'Setup is account-native: ownership, land description and connections. Scores stay on Overview and the analysis pages.'),
    step('[data-tour="setup-details"]', 'Property details',
      'Name, tenure, land use, species and management notes. Edit here; the analysis pages read the published run, not this form.'),
    step('[data-tour="setup-data"]', 'Your data',
      'Download a spreadsheet, edit the cells you own, and upload it back. Scores and area stay read-only.'),
    step('[data-tour="setup-services"]', 'Third-party services',
      'Connect or withdraw services that receive a copy of your property record, with an auditable consent trail.'),
  ],
  glossary: [
    step('[data-tour="nav-help"]', 'Help on every page',
      'The question mark is always in the top bar. Next we will open the drawer so you can see search and the glossary.',
      { openHelpOnNext: true }),
    step('[data-tour="help-search"]', 'Search the glossary',
      'Type EDX, TNFD, TWI or any other term. Matches highlight in the list below.',
      { waitForElement: 6000 }),
    step('[data-tour="help-glossary"]', 'Glossary',
      'Short definitions for the language used on Overview, Map, Disclosure and Reports. Open this whenever a label is new to you.',
      { waitForElement: 4000 }),
    step('[data-tour="help-scores"]', 'How to read the scores',
      'On property pages this primer marks your Land Health band and brittleness so the tables match what you are looking at.',
      { optional: true }),
  ],
};

export function getTourSteps(sectionId) {
  return SECTION_STEPS[sectionId] || [];
}
